import { useEffect, useState } from 'react';
import useAuth from './hooks/useAuth';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './components/Dashboard';
import KbDocuments from './components/KbDocuments';
import AiAndRetrievalConfig from './components/AiAndRetrievalConfig';
import OrchestratorConfig from './components/OrchestratorConfig';
import Analytics from './components/Analytics';
import PortfolioPreviewAnalytics from './components/PortfolioPreviewAnalytics';
import OperationalLogs from './components/OperationalLogs';
import DialogSessions from './components/DialogSessions';
import AuditLog from './components/AuditLog';
import Reports from './components/Reports';
import Legend from './components/Legend';
import { DemoProvider } from './contexts/DemoContext';
import useTheme from './hooks/useTheme';

const IS_PORTFOLIO_PREVIEW = import.meta.env.VITE_PORTFOLIO_PREVIEW_MODE === 'true';

const PLACEHOLDER_PAGES = new Set([]);

function Placeholder({ page }) {
  return (
    <div className="flex h-full items-center justify-center text-ai-text-muted">
      <div className="text-center">
        <p className="text-4xl mb-4">🚧</p>
        <p className="text-lg font-semibold">В разработке</p>
        <p className="text-sm mt-2">Панель «{page}» будет реализована в следующем спринте.</p>
      </div>
    </div>
  );
}

function App() {
  const { isReady, isLoggedIn, isDemo, login, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [activePage, setActivePage] = useState('dashboard');

  // Auto-login in portfolio preview mode using the configured demo token.
  useEffect(() => {
    if (IS_PORTFOLIO_PREVIEW && !isLoggedIn) {
      const demoToken = import.meta.env.VITE_ADMIN_DEMO_TOKEN;
      if (demoToken) {
        login(demoToken);
      }
    }
  }, [isLoggedIn, login]);

  if (!isReady) {
    return null;
  }

  if (!isLoggedIn) {
    return <Login onLogin={login} />;
  }

  // Portfolio preview mode bypasses routing and shows the simplified analytics screen.
  if (IS_PORTFOLIO_PREVIEW) {
    return (
      <DemoProvider isDemo={isDemo}>
        <PortfolioPreviewAnalytics />
      </DemoProvider>
    );
  }

  const renderContent = () => {
    if (PLACEHOLDER_PAGES.has(activePage)) {
      return <Placeholder page={activePage} />;
    }
    if (activePage === 'dashboard') return <Dashboard />;
    if (activePage === 'kb') return <KbDocuments />;
    if (activePage === 'ai-config') return <AiAndRetrievalConfig />;
    if (activePage === 'orchestrator') return <OrchestratorConfig />;
    if (activePage === 'logs') return <OperationalLogs />;
    if (activePage === 'dialogs') return <DialogSessions />;
    if (activePage === 'analytics') return <Analytics />;
    if (activePage === 'audit') return <AuditLog />;
    if (activePage === 'reports') return <Reports />;
    if (activePage === 'legend') return <Legend />;
    return <Dashboard />;
  };

  return (
    <DemoProvider isDemo={isDemo}>
      <div className="ai-layout">
        <Sidebar
          active={activePage === 'kb' ? 'kb' : activePage}
          onChange={setActivePage}
          onLogout={logout}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
        <div className="ai-main">
          <div className="ai-header__wrapper">
            <header className="ai-header">
              <div>
                <div className="ai-header__title">Admin Console</div>
                <div className="ai-header__subtitle">FastAPI · консоль наблюдаемости</div>
              </div>
              <div className="ai-header__brand">Zerocoder</div>
            </header>
          </div>
          <main className="ai-content">
            <ErrorBoundary key={activePage}>
              {renderContent()}
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </DemoProvider>
  );
}

export default App;
